<template>
  <div class="workbench">
    <workbench-toolbar
      :name="targetName"
      :activated="activated"
      :metrics="metrics"
      :loading="loading"
      :menu="headerMenu"
      :showGraphs="showGraphs">
    </workbench-toolbar>

    <div class="workbench__top-pane">
      <div class="workbench__side-panel" v-if="!live" :class="{'is-collapsed': isCollapsed, 'is-expanded-view': isExpandedView}">
        <attribute-panel @collapse-clicked="togglePanelCollapse" @change-size-click="togglePanelSize" :is-expanded-view="isExpandedView" :is-collapsed="isCollapsed"></attribute-panel>
      </div>

      <div id="canvas" class="workbench__canvas">
        <div v-if="loading" class="workbench__loader">
          <loader-dots></loader-dots>
        </div>
        <scroll-panel class="workbench__canvas-content">
          <slot></slot>
        </scroll-panel>
      </div>
    </div>

    <div class="workbench__bottom-pane" :class="[sizeClass]">
      <quick-add-attributes v-if="showQuickAddAttributebuttons" :viewOnly="viewOnly"></quick-add-attributes>
      <target-definition namespace="las"></target-definition>
    </div>

    <div class="workbench__bottom-bar">
      <bottom-pane-sizer :current-size="currentBottomPaneSize" @new-size="changeBottomPaneSize"></bottom-pane-sizer>
      <definition-actions
        v-if="!live"
        :allowDownloadBtn="allowDownloadBtn"
      ></definition-actions>
    </div>

    <transparent-control-panel
      v-if="showRevisionHistoryPanel"
      :initialPosition="{
        top: 125,
        right: 50
      }">
      <vuex-revision-history-panel
        :onSetCurrentMove="setTargetDefinitionFromRevisionHistory">
      </vuex-revision-history-panel>
    </transparent-control-panel>
  </div>
</template>

<script>
  /**
   * 1. the attribute panel is the main component that we will need
   * 2. we will also need the target-definition, quick-add-attributes component as well....
   */
  import ScrollPanel from '@/components/common/scrollPanel'
  import QuickAddAttributes from '@/appComponents/oldWorkbench/quickAddAttributes/QuickAddAttributes'
  import TargetDefinition from '@/appComponents/oldWorkbench/targetDefinition'
  import AttributePanel from '@/appComponents/oldWorkbench/attributePanel/AttributePanel'
  import DefinitionActions from '@/appComponents/oldWorkbench/definitionActions/DefinitionActions'
  import BottomPaneSizer from '@/appComponents/oldWorkbench/bottomPaneSizer/BottomPaneSizer'
  import LoaderDots from '@/components/common/loaderDots'
  import WorkbenchToolbar from '@/appComponents/oldWorkbench/workbenchToolbar/WorkbenchToolbar'
  import Dashboard from '@/appComponents/common/AudienceHubDashboardHeader'
  import VuexRevisionHistoryPanel from '@/appComponents/revisionHistory/VuexRevisionHistoryPanel'
  import { TransparentControlPanel } from '@/components/common/controlPanel'
  import { SET_TARGET_ENVIRONMENT } from './store/WorkbenchWCanvasSlotActionsGenerator'
  import { ACTIVATION_STATUS } from '@/appLogic/targets/Constants.js'

  export default {
    components: {
      ScrollPanel,
      AttributePanel,
      WorkbenchToolbar,
      QuickAddAttributes,
      TargetDefinition,
      DefinitionActions,
      BottomPaneSizer,
      LoaderDots,
      Dashboard,
      VuexRevisionHistoryPanel,
      TransparentControlPanel
    },
    props: {
      loading: {
        type: Boolean
      },
      showGraphs: {
        type: Boolean,
        default: false
      },
      metrics: {
        type: Object
      },
      targetName: {
        type: String
      },
      activated: {
        type: Boolean
      },
      archived: {
        type: Boolean
      },
      headerMenu: {
        type: Array,
        required: true
      },
      showQuickAddAttributes: {
        type: Boolean,
        default: true
      },
      allowDownloadBtn: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        isCollapsed: false,
        isExpandedView: false,
        currentBottomPaneSize: 'medium',
        viewershipRowHeight: null,
        universeRowHeight: null
      }
    },
    computed: {
      sizeClass () {
        return `is-${this.currentBottomPaneSize}`
      },
      showRevisionHistoryPanel () {
        let s = this.$store.state
        return s.las ? s.las.showTargetHistoryPanel : (
          s.ad ? s.ad.showTargetHistoryPanel : s.sm.showTargetHistoryPanel
        )
      },
      showQuickAddAttributebuttons () {
        return this.showQuickAddAttributes ? !this.live : false
      },
      live () {
        if (this.$store.state.currentTarget.currentTarget) {
          return this.$store.state.currentTarget.currentTarget.activation_status === ACTIVATION_STATUS.LIVE
        }
      },
      viewOnly () {
        return this.live || this.$store.state.currentTarget.activation_status === ACTIVATION_STATUS.SUBMITTED
      }
    },
    methods: {
      togglePanelCollapse () {
        this.isCollapsed = !this.isCollapsed
      },
      togglePanelSize () {
        this.isExpandedView = !this.isExpandedView
      },
      changeBottomPaneSize (size) {
        this.currentBottomPaneSize = size
      },
      setTargetDefinitionFromRevisionHistory (rh) {
        this.$store.dispatch(SET_TARGET_ENVIRONMENT, {
          targetDefinition: rh.target_definition,
          targetData: rh.target_data
        })
      },
    }
  }
</script>

<style lang="scss">
  .workbench-resize {
    font-size: rem(12);

    body {
      font-size: 1rem;
    }

    @include respond-to('medium and up') {
      font-size: rem(13.5);
    }

    @include respond-to('large and up') {
      font-size: rem(15);
    }
  }
  .workbench {
    $side-panel-width: rem(380);
    $audience-size-width: rem(140);
    $margin-size: rem(10);

    display: flex;
    flex-direction: column;
    height: 100%;

    h4 {
      margin: 0 0 15px;
      text-transform: uppercase;
    }

    &__top-pane {
      display: flex;
      height: 100%;
    }

    &__bottom-pane {
      flex: 1 0 auto;
      display: flex;
      flex-direction: column;
      color: white;
      background: color(blue-black, darker);
      transition: height .25s $global-easing;

      &.is-small {
        height: 0;
        overflow: hidden;
      }

      &.is-medium {
        height: rem(190);
      }

      &.is-large {
        height: rem(450);
      }
    }

    &__bottom-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex: 1 0 auto;
      height: rem(58);
      padding: 0 rem(20);
      background: color(blue-black, darker);
    }

    &__canvas {
      position: relative;
      display: flex;
      width: 100%;
    }

    &__canvas-content {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      min-width: rem(980);
      padding: 0 $margin-size;
      box-sizing: border-box;
      overflow-y: scroll;
      overflow-x: hidden;
    }

    &__loader {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      @if ($workbench-loader) {
        z-index: $workbench-loader;
      } @else {
        z-index: 9999;
      }
      background-color: rgba(#ffffff, .8);
    }

    .attr-panel {
      height: 100%;
    }

    &__side-panel {
      position: relative;
      flex: none;
      width: $side-panel-width;
      margin-left: auto;
      transition: width .25s $global-easing;

      &.is-expanded-view {
        width: rem(600);
      }
      &.is-collapsed {
        width: rem(30);
      }
    }
  }
</style>



// WEBPACK FOOTER //
// WorkbenchWCanvasSlot.vue?1e6d1434